import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import CTABand from '@/components/sections/CTABand';
import { Heart, Recycle, HandHeart, Mail, Search, Box, Truck, ArrowUpRight, ArrowRight } from 'lucide-react';
import { BRAND } from '@/data/site';
import { POSTS, formatDate } from '@/blog/posts';

const STEPS = [
  {
    icon: Search,
    title: 'Identify',
    body: 'During every cleanout, our crew flags items that are still useful, including furniture, appliances, household goods, and building materials.',
  },
  {
    icon: Box,
    title: 'Sort',
    body: 'Usable items are set aside on-site, kept dry, and prepared for routing to a donation partner instead of the dumpster.',
  },
  {
    icon: Truck,
    title: 'Deliver',
    body: 'We coordinate with local Indiana nonprofits, shelters, and families in need. Documentation is available on request.',
  },
];

export default function HearthProject() {
  return (
    <>
      <SeoHead
        title="The Hearth Project | Ohana Property Services"
        description="The Hearth Project is Ohana Property Services' donation-conscious recovery initiative that diverts usable items from cleanouts to Indiana nonprofits, shelters, and families in need."
        canonical="https://ohanapropertyexperts.com/hearth-project"
      />

      <section className="relative overflow-hidden bg-ohana-primary text-ohana-bg">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1732800577634-f2e5c4d43995?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000)',
          }}
          aria-hidden="true"
        />
        <div className="relative container-ohana pt-24 pb-20 md:pt-32 md:pb-28">
          <p className="overline">An Initiative by Ohana</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            The Hearth Project.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ohana-bg/80 max-w-2xl">
            Every cleanout creates an opportunity. We chose to make ours an opportunity for someone else.
          </p>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-ohana-text">
            <p>
              The Hearth Project is the heart of how we work. When a family transitions out of a property, what's left behind isn't just stuff. It's the makings of someone else's fresh start. A working dresser. A near-new mattress. A box of pantry goods. Tools. Toys. Books.
            </p>
            <p>
              Instead of letting it all go to the landfill, we route what's useful to local Indiana nonprofits, shelters, and families who need it most. The hearth, that warm central place in every home, is what we're trying to rebuild for someone with what's left over from someone else.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-ohana-border bg-ohana-surface p-8">
              <Heart className="h-10 w-10 text-ohana-accent" />
              <p className="mt-4 font-serif text-2xl text-ohana-primary leading-snug">
                Built into every cleanout. No extra cost. Just the right thing to do.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-surface" data-testid="hearth-how-it-works">
        <div className="container-ohana">
          <div className="max-w-3xl">
            <p className="overline">How Donation Recovery Works</p>
            <h2 className="h-display text-4xl md:text-5xl mt-3 leading-tight">
              Three steps from cleanout to community.
            </h2>
            <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
              Usable items are identified during cleanouts, sorted on-site, and donated to local Indiana nonprofits, shelters, and families in need.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.title} className="card-default !p-7 bg-white" data-testid={`hearth-step-${i + 1}`}>
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-ohana-accent text-white">
                  <s.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ohana-primary leading-snug">{s.title}</h3>
                <p className="mt-3 text-ohana-text-muted leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-bg" data-testid="hearth-partner">
        <div className="container-ohana">
          <div className="rounded-3xl bg-ohana-primary p-10 md:p-16 text-ohana-bg">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-ohana-accent text-white">
                  <HandHeart className="h-6 w-6" />
                </span>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl leading-tight">
                  Are you a nonprofit or community organization?
                </h2>
                <p className="mt-4 text-ohana-bg/80 text-base md:text-lg leading-relaxed max-w-2xl">
                  We're actively building a network of Indiana donation partners. If your organization could use furniture, household goods, appliances, or building materials, we'd love to talk about how we can route them your way.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <a
                  href={`mailto:${BRAND.email}?subject=Hearth%20Project%20Donation%20Partner%20Inquiry`}
                  className="inline-flex items-center justify-center rounded-full bg-ohana-accent px-7 py-3.5 text-base font-bold text-white transition hover:bg-ohana-accent-dark"
                  data-testid="hearth-partner-email"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Reach Out
                </a>
                <p className="mt-3 text-sm text-ohana-bg/60">{BRAND.email}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-ohana-surface" data-testid="hearth-blog-index">
        <div className="container-ohana">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="overline">From The Hearth Project Blog</p>
              <h2 className="h-display text-4xl md:text-5xl mt-3 leading-tight">
                Field notes and cleanout guides.
              </h2>
              <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
                Practical reading for homeowners, families, and investors navigating property transitions across Central Indiana.
              </p>
            </div>
            <Link
              to="/blog"
              className="self-start inline-flex items-center gap-2 text-ohana-primary font-bold underline decoration-ohana-accent decoration-2 underline-offset-4 hover:text-ohana-accent-dark"
              data-testid="hearth-view-all-posts"
            >
              View all posts
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7" data-testid="hearth-blog-grid">
            {POSTS.map((p, i) => {
              const featured = i === 0;
              return (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className={`group rounded-2xl border border-ohana-border bg-white p-7 md:p-8 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-ohana-primary/5 ${
                    featured ? 'md:col-span-2' : ''
                  }`}
                  data-testid={`hearth-blog-card-${p.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-ohana-surface px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-primary">
                        {p.category}
                      </span>
                      {featured && (
                        <span className="inline-flex items-center rounded-full bg-ohana-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-accent">
                          Featured
                        </span>
                      )}
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-ohana-primary transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <h3
                    className={`mt-5 font-serif text-ohana-primary leading-tight ${
                      featured ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'
                    }`}
                  >
                    {p.title}
                  </h3>
                  <p className="mt-4 text-ohana-text-muted leading-relaxed">{p.excerpt}</p>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-ohana-text-muted">
                    {formatDate(p.date)}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Donation-Conscious Cleanouts"
        title="Hire a cleanout that does more."
        subtitle="When you choose Ohana, you choose a cleanout that gives back. Built into every job."
        ctaLabel="Get a Free Quote"
      />
    </>
  );
}
